import { useSelector } from "react-redux";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { EmployeeSelector } from "../../../components/utils/moduleSelectors/employeeSelector";
import { useTranslation } from "react-i18next";
import { UserIcon } from "@heroicons/react/24/outline";
import { HoursViewComponent } from "../../../components/dashboard/hours/hours_view_component";
import { HourEditDialog } from "../../../components/dashboard/hours/hour_edit_dialog";

export const HoursView = () => {
  const currentUser = useSelector((state) => state.session.currentUser);
  const [user, setUser] = useState(currentUser);
  const [deletedHourId, setDeletedHourId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = useSelector((state) =>
    state.session.currentUser?.roles?.some((r) => r.name === "admin")
  );
  const isManager = useUserHasAccess({ role: "hours", action: "manage" });
  const userMemo = useMemo(
    () => (isAdmin || isManager ? user : currentUser),
    [user?.id, isAdmin, isManager, currentUser?.id]
  );
  const user_id = useMemo(() => userMemo?.id, [userMemo?.id]);
  const [hourFormId, setHourFormId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastChangedHour, setLastChangedHour] = useState(null);
  const [newHourPrefill, setNewHourPrefill] = useState(null);
  const [currentViewDate, setCurrentViewDate] = useState(null);
  const { t } = useTranslation();

  // URL params for view and period
  const initialViewParam = useMemo(
    () => searchParams.get("view") || "week",
    [searchParams]
  );
  const initialDateParam = useMemo(
    () => searchParams.get("date") || null,
    [searchParams]
  );
  
  // Initialize current view date from URL param
  useEffect(() => {
    if (initialDateParam) {
      setCurrentViewDate(initialDateParam);
    }
  }, [initialDateParam]);
  const hourIdParam = useMemo(
    () => searchParams.get("hourId"),
    [searchParams]
  );

  // Auto-open dialog if hourId is in URL
  useEffect(() => {
    if (hourIdParam) {
      setHourFormId(hourIdParam);
      setDialogOpen(true);
      // Clean up URL param after opening
      const current = Object.fromEntries(searchParams.entries());
      delete current.hourId;
      setSearchParams(current, { replace: true });
    }
  }, [hourIdParam, searchParams, setSearchParams]);
  const initialFromParam = useMemo(
    () => searchParams.get("from") || null,
    [searchParams]
  );
  const initialToParam = useMemo(
    () => searchParams.get("to") || null,
    [searchParams]
  );
  const initialPeriodParam = useMemo(
    () => searchParams.get("period") || "month",
    [searchParams]
  );

  const updateUrlParams = (updates) => {
    const current = Object.fromEntries(searchParams.entries());
    const next = { ...current, ...updates };
    // Clean up params when switching modes
    if (next.view === "day" || next.view === "week") {
      delete next.from;
      delete next.to;
      if (!next.date) delete next.date;
    }
    if (next.view === "period") {
      delete next.date;
    }
    setSearchParams(next);
  };

  const handleHourClick = (hourId) => {
    setHourFormId(hourId);
    setDialogOpen(true);
  };

  const handleNewHour = (date = null) => {
    setHourFormId(null);
    setDialogOpen(true);
    // Store the date for prefill - we'll pass it via prefill prop
    if (date) {
      setNewHourPrefill({ date, user_id: userMemo?.id, user: userMemo });
    } else {
      // Use current view date from component state, or fallback to URL param or today
      const currentDate = currentViewDate || initialDateParam || new Date().toISOString().substring(0, 10);
      setNewHourPrefill({ date: currentDate, user_id: userMemo?.id, user: userMemo });
    }
  };

  const Content = () => {
    const handleStateChange = ({ view, date, date_from, date_to, period_type }) => {
      // Track current date for new hour creation
      if (date) {
        setCurrentViewDate(date);
      }
      if (view === "period") {
        updateUrlParams({ view, from: date_from, to: date_to, period: period_type });
      } else {
        updateUrlParams({ view, date });
      }
    };
    return (
      <div className="space-y-6 mt-10">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{t("Hours Management")}</h2>
            <button
              onClick={() => handleNewHour()}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <span className="mr-2">+</span>
              {t("New Hour")}
            </button>
          </div>
          {/* User Selection */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("Select Employee")}
            </label>
            {isAdmin || isManager ? (
              <EmployeeSelector
                name="user_id"
                selected={user}
                filters={[{ name: "employee", value: true }]}
                onChange={(data) => {
                  setUser(data?.target?.object);
                  setHourFormId(null);
                  if (data?.target?.object?.id) {
                    updateUrlParams({ user_id: data.target.object.id });
                  }
                }}
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium">
                  {user?.displayTitle}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Hours View Component */}
        <HoursViewComponent
          user_id={user_id}
          onHourClick={handleHourClick}
          onNewHourClick={handleNewHour}
          showFilters={true}
          initialView={initialViewParam}
          initialDate={initialDateParam}
          initialDateFrom={initialFromParam}
          initialDateTo={initialToParam}
          initialPeriod={initialPeriodParam}
          onStateChange={handleStateChange}
          pushHour={lastChangedHour}
          removeHourId={deletedHourId}
        />
        <HourEditDialog
          open={dialogOpen}
          hourId={hourFormId}
          prefill={hourFormId ? null : newHourPrefill}
          onClose={() => { setDialogOpen(false); setHourFormId(null); setNewHourPrefill(null); }}
          onSaved={(saved) => { setLastChangedHour(saved); setDialogOpen(false); setHourFormId(null); setNewHourPrefill(null); }}
          onDeleted={(id) => { setDialogOpen(false); setHourFormId(null); setNewHourPrefill(null); setDeletedHourId(id); }}
        />
      </div>
    );
  };

  return (
    <DashboardHeader
      title={t("Hours Overview")}
      subtitle={t("Track and manage work hours")}
    >
      <div className="min-h-screen bg-gray-50">
        <Content />
      </div>
    </DashboardHeader>
  );
};
